import { useCallback, useEffect, useRef } from 'react'

/**
 * Уровень голоса участников звонка для анимации вокруг аватара.
 *
 * Ресурсы экономятся четырьмя решениями: один AudioContext на весь звонок,
 * analyser с маленьким fftSize (нужна громкость, а не спектр), опрос по
 * таймеру ~15 раз в секунду вместо requestAnimationFrame и — главное —
 * значения **не проходят через React**. Они лежат в ref и раздаются
 * подписчикам, которые пишут их прямо в CSS-переменную: иначе весь звонок
 * шёл бы поток перерисовок с частотой опроса.
 */

const POLL_INTERVAL_MS = 65
/** Ниже этого уровня считаем тишиной: микрофон всегда шумит на 2–4%. */
const SILENCE_THRESHOLD = 0.06

type AnalyserEntry = {
  analyser: AnalyserNode
  source: MediaStreamAudioSourceNode
  /** Буфер под getByteFrequencyData: явный ArrayBuffer, SharedArrayBuffer тип не примет. */
  data: Uint8Array<ArrayBuffer>
}

export type VoiceActivityLevels = Record<number, number>

export type VoiceLevelsListener = (levels: VoiceActivityLevels) => void

export function useVoiceActivity() {
  const contextRef = useRef<AudioContext | null>(null)
  const entriesRef = useRef<Map<number, AnalyserEntry>>(new Map())
  const timerRef = useRef<number | null>(null)
  const levelsRef = useRef<VoiceActivityLevels>({})
  const listenersRef = useRef<Set<VoiceLevelsListener>>(new Set())

  const notify = useCallback(() => {
    listenersRef.current.forEach((listener) => listener(levelsRef.current))
  }, [])

  const ensureContext = useCallback((): AudioContext | null => {
    if (contextRef.current) return contextRef.current

    const AC =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
    if (!AC) return null

    // Создаётся уже после жеста пользователя (кнопка «Позвонить»/«Принять»),
    // иначе Safari держит контекст в suspended и уровни всегда нулевые.
    contextRef.current = new AC()
    return contextRef.current
  }, [])

  const tick = useCallback(() => {
    const levels: VoiceActivityLevels = {}

    entriesRef.current.forEach((entry, userId) => {
      entry.analyser.getByteFrequencyData(entry.data)

      let sum = 0
      for (let i = 0; i < entry.data.length; i += 1) {
        sum += entry.data[i]
      }
      const average = entry.data.length > 0 ? sum / entry.data.length / 255 : 0
      levels[userId] = average < SILENCE_THRESHOLD ? 0 : Math.min(1, average * 2.5)
    })

    levelsRef.current = levels
    notify()
  }, [notify])

  const startTimer = useCallback(() => {
    if (timerRef.current !== null) return
    timerRef.current = window.setInterval(tick, POLL_INTERVAL_MS)
  }, [tick])

  const stopTimer = useCallback(() => {
    if (timerRef.current === null) return
    window.clearInterval(timerRef.current)
    timerRef.current = null
  }, [])

  /** Подписка на уровни: возвращает функцию отписки. */
  const subscribe = useCallback((listener: VoiceLevelsListener): (() => void) => {
    listenersRef.current.add(listener)
    listener(levelsRef.current)

    return () => {
      listenersRef.current.delete(listener)
    }
  }, [])

  /** Подключить поток участника к анализатору. */
  const trackStream = useCallback(
    (userId: number, stream: MediaStream) => {
      if (stream.getAudioTracks().length === 0) return

      const context = ensureContext()
      if (!context) return

      // Safari возобновляет контекст только после жеста; вызов безопасен и в running.
      void context.resume().catch(() => undefined)

      const existing = entriesRef.current.get(userId)
      if (existing) {
        existing.source.disconnect()
        existing.analyser.disconnect()
        entriesRef.current.delete(userId)
      }

      const analyser = context.createAnalyser()
      analyser.fftSize = 256
      analyser.smoothingTimeConstant = 0.7

      const source = context.createMediaStreamSource(stream)
      source.connect(analyser)

      entriesRef.current.set(userId, {
        analyser,
        source,
        data: new Uint8Array(new ArrayBuffer(analyser.frequencyBinCount)),
      })

      startTimer()
    },
    [ensureContext, startTimer],
  )

  const untrackStream = useCallback(
    (userId: number) => {
      const entry = entriesRef.current.get(userId)
      if (!entry) return

      entry.source.disconnect()
      entry.analyser.disconnect()
      entriesRef.current.delete(userId)

      if (entriesRef.current.size === 0) {
        stopTimer()
        levelsRef.current = {}
        notify()
      }
    },
    [notify, stopTimer],
  )

  const reset = useCallback(() => {
    stopTimer()
    entriesRef.current.forEach((entry) => {
      entry.source.disconnect()
      entry.analyser.disconnect()
    })
    entriesRef.current.clear()
    levelsRef.current = {}
    notify()

    const context = contextRef.current
    contextRef.current = null
    if (context) {
      void context.close().catch(() => undefined)
    }
  }, [notify, stopTimer])

  useEffect(() => reset, [reset])

  return { trackStream, untrackStream, reset, subscribe }
}
